import { Server } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { IssuedToken } from "@/components/IssuedToken";
import { useApp } from "@/lib/AppContext";
import { ApiError, type IssuedWorker, type WorkerNode } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

/**
 * Settings → Worker, completed (SEC-3, UI-13).
 *
 * M2.1 built the half without which nothing works at all — minting a token, revoking one
 * — and said the rest was UI-13's. This is the rest: what each machine actually is, and
 * how work reaches it.
 *
 * The GPU, the operating system, the sidecar and the steps a machine offers all come
 * from the machine itself, on the claim poll it was already making. None of it is
 * configured here: a settings screen that let somebody type in a GPU name would be a
 * settings screen that can be wrong about the hardware.
 *
 * The token is shown once and never again, because the Server keeps only its hash. That
 * is a real constraint rather than a policy, and the screen says it plainly instead of
 * leaving somebody to discover it by closing the panel.
 */
export function WorkerSettings({ summary }: { summary: string }) {
  const { api } = useApp();

  const [workers, setWorkers] = useState<WorkerNode[] | null>(null);
  const [issued, setIssued] = useState<IssuedWorker | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setWorkers(await api.listWorkers());
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The workers could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function register(event: React.FormEvent) {
    event.preventDefault();
    if (busy || name.trim().length === 0) return;

    setBusy(true);
    setProblem(null);

    try {
      setIssued(await api.registerWorker(name.trim()));
      setName("");
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The worker could not be registered.");
    } finally {
      setBusy(false);
    }
  }

  /** 0068 — pause or resume. Same shape as revoking: one call, then the list again. */
  async function setPaused(worker: WorkerNode, paused: boolean) {
    setBusy(true);
    setProblem(null);

    try {
      if (paused) await api.pauseWorker(worker.id);
      else await api.resumeWorker(worker.id);
      await refresh();
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError
          ? cause.message
          : `The worker could not be ${paused ? "paused" : "resumed"}.`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function revoke(worker: WorkerNode) {
    setBusy(true);
    setProblem(null);

    try {
      await api.revokeWorker(worker.id);
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The worker could not be revoked.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Worker
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem}
        </p>
      )}

      {issued && (
        <IssuedToken issued={issued} noun="machine" onDismiss={() => setIssued(null)}>
          Put it in the Worker's configuration as <code className="font-mono">Worker__Token</code>,
          alongside <code className="font-mono">Worker__ServerUrl</code>.
        </IssuedToken>
      )}

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          Connect a worker
        </h2>
        <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          The PC that claims jobs. It reaches out to this Server rather than listening, so it works
          from behind a home router with nothing opened up.
        </p>

        <form className="mt-[var(--space-4)] flex gap-[var(--space-3)]" onSubmit={(event) => void register(event)}>
          <label className="sr-only" htmlFor="worker-name">
            Worker name
          </label>
          <input
            id="worker-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Studio PC"
            maxLength={80}
            className="min-w-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
          />
          <button
            type="submit"
            disabled={busy || name.trim().length === 0}
            className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            Register
          </button>
        </form>
      </section>

      <section>
        <h2 className="mb-[var(--space-3)] text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          Registered machines
        </h2>

        {workers === null && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
        )}

        {workers?.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            No worker yet. Until one is connected, uploaded recordings wait in the queue rather than
            being processed.
          </p>
        )}

        <ul className="flex flex-col gap-[var(--space-2)]">
          {workers?.map((worker) => (
            <li
              key={worker.id}
              className="flex items-center gap-[var(--space-4)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)]"
            >
              <Server size={16} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
                  {worker.name ?? "Unnamed worker"}
                </p>
                <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                  {describeContact(worker)}
                </p>
                <Machine worker={worker} />
              </div>

              <span
                className={cn(
                  "shrink-0 rounded-[var(--radius-full)] px-[var(--space-2)] py-[2px] text-[length:var(--text-xs)] font-semibold",
                  worker.status !== "active"
                    ? "bg-[var(--surface-2)] text-[var(--text-muted)]"
                    : worker.needsUpdate || worker.pausedAt
                      ? "bg-[var(--surface-2)] text-[var(--warning)]"
                      : "bg-[var(--surface-2)] text-[var(--text-primary)]",
                )}
              >
                {badge(worker)}
              </span>

              {worker.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void setPaused(worker, !worker.pausedAt)}
                  className="shrink-0 rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
                >
                  {worker.pausedAt ? "Resume" : "Pause"}
                </button>
              )}

              {worker.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void revoke(worker)}
                  className="shrink-0 rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          How work is handed out
        </h2>
        <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          A machine asks for work every few seconds and holds a short lease on whatever it is given,
          renewing it while it works — so "the lease expired" means one thing only: the machine
          stopped talking, and the step goes back on the queue within a minute. A step handed out
          too many times fails instead of going round again, and one that runs far past its time
          limit is stopped and tried again.
        </p>
        <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          Pause a machine before updating its GPU driver or restarting it: it finishes the step it
          is on and takes nothing new until you resume it.
        </p>
        <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Those timings are the Server's, deliberately. They are what crash-safety rests on, and a
          machine that chose its own lease could choose one long enough that nobody notices it has
          abandoned a step.
        </p>
      </section>

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          Restarting the pipeline at a step
        </h2>
        <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          The control sits on each recording rather than here, because that is the question it
          answers: <em>this</em> transcript came out wrong, run it again from the step that produced
          it. Open a recording and it is beside the pipeline summary.
        </p>
      </section>
    </div>
  );
}

/**
 * What a machine says about itself, when it has said anything.
 *
 * Empty until it has asked for work once, and then drawn as nothing at all rather than
 * as a row of dashes: a machine that was registered and never started has nothing to
 * report, and placeholders for it would read as a machine that reported nothing useful.
 */
function Machine({ worker }: { worker: WorkerNode }) {
  const facts = [
    worker.machine["gpu"],
    worker.machine["os"],
    worker.machine["sidecar"] ? `sidecar ${worker.machine["sidecar"]}` : null,
  ].filter(Boolean);

  if (facts.length === 0 && !worker.machine["steps"]) return null;

  return (
    <>
      {facts.length > 0 && (
        <p className="truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {facts.join(" · ")}
        </p>
      )}

      {worker.machine["steps"] && (
        <p className="truncate font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          {worker.machine["steps"]}
        </p>
      )}
    </>
  );
}

function badge(worker: WorkerNode): string {
  if (worker.status !== "active") return "Revoked";
  if (worker.needsUpdate) return "Needs updating";
  if (worker.pausedAt) return "Paused";

  return "Active";
}

function describeContact(worker: WorkerNode): string {
  if (worker.status === "revoked")
    return `Revoked ${formatDate(worker.revokedAt) || "recently"} — this machine can no longer claim work.`;

  if (worker.needsUpdate)
    return "Too old for this Server to hand work to. Update the Worker on this machine and it picks up again.";

  if (worker.pausedAt)
    return worker.busy
      ? "Paused — finishing its current step, then taking nothing new."
      : "Paused — taking no work.";

  return worker.lastHeartbeatAt === null
    ? "Registered, but it has never connected."
    : `Last asked for work ${formatDate(worker.lastHeartbeatAt)}.`;
}
